// src/pages/LoginPage.tsx
// Login and signup page with Track15 branding
import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import { Track15Logo } from "@/components/brand";
import { TERMINOLOGY } from "@/config/terminology";
import { useAuth } from "@/context/AuthContext";
import {
  sanitizePostAuthRedirect,
  signInWithGoogle,
} from "@/services/authService";

type AuthMode = "signin" | "signup";

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const initialMode =
    searchParams.get("mode") === "signup" ? "signup" : "signin";

  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const { login, register, user, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Update mode when URL changes
  useEffect(() => {
    const urlMode = searchParams.get("mode") === "signup" ? "signup" : "signin";
    setMode(urlMode);
  }, [searchParams]);

  // Detect OAuth errors from redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get("error");
    if (authError) {
      setError(authError);
    }
  }, []);

  // Get the return URL from navigation state, or default to the platform landing page.
  const from = sanitizePostAuthRedirect(
    (location.state as { redirectTo?: string })?.redirectTo,
  );

  useEffect(() => {
    if (!isAuthLoading && user) {
      void navigate(from, { replace: true });
    }
  }, [from, isAuthLoading, navigate, user]);

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      setError(null);
      await signInWithGoogle(from);
      // Redirect happens via OAuth flow — user leaves the page
    } catch (err) {
      setError(err instanceof Error ? err.message : "Google sign-in failed");
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (mode === "signin") {
        await login(email, password);
      } else {
        if (!firstName.trim() || !lastName.trim()) {
          throw new Error("Please enter your first and last name");
        }
        await register({
          email,
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
      }
      // Redirect to the page they were trying to access
      void navigate(from, { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mode === "signin"
            ? "Sign in failed. Please try again."
            : "Sign up failed. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === "signin" ? "signup" : "signin");
    setError(null);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f4f6fc]">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-3">
            <Track15Logo size={180} variant="full" color="navy" />
          </div>
          <p className="text-xs text-slate-500 mb-2">
            {TERMINOLOGY.platform.tagline}
          </p>
          <p className="mt-3 text-sm text-slate-600">
            {mode === "signin"
              ? "Sign in to your workspace"
              : `Create your ${TERMINOLOGY.platform.name} account`}
          </p>
        </div>

        {/* Google Sign-In */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
          className="mb-4 flex w-full items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50"
        >
          {googleLoading ? (
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
          ) : (
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          )}
          Sign in with Google
        </button>

        {/* Separator */}
        <div className="mb-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs text-slate-400">or</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        {/* Mode Toggle */}
        <div className="mb-6 flex rounded-lg bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setMode("signin")}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-all ${
              mode === "signin"
                ? "bg-white text-[#4359a6] shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => setMode("signup")}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-all ${
              mode === "signup"
                ? "bg-white text-[#4359a6] shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name fields for signup */}
          {mode === "signup" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-slate-700"
                >
                  First name
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  autoComplete="given-name"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-[#4359a6] focus:outline-none focus:ring-1 focus:ring-[#4359a6]"
                  placeholder="John"
                />
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-slate-700"
                >
                  Last name
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  autoComplete="family-name"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-[#4359a6] focus:outline-none focus:ring-1 focus:ring-[#4359a6]"
                  placeholder="Smith"
                />
              </div>
            </div>
          )}

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-slate-700"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-[#4359a6] focus:outline-none focus:ring-1 focus:ring-[#4359a6]"
              placeholder="you@example.com"
            />
            {email.toLowerCase().endsWith("@track15.com") && (
              <p className="mt-1 text-xs text-amber-600">
                Track15 team members should use the Google Sign-In button above.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-slate-700"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={
                mode === "signin" ? "current-password" : "new-password"
              }
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-[#4359a6] focus:outline-none focus:ring-1 focus:ring-[#4359a6]"
              placeholder="••••••••"
            />
            {mode === "signup" && (
              <p className="mt-1 text-xs text-slate-500">
                Must be at least 8 characters
              </p>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-md bg-[#4359a6] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#2d3f74] focus:outline-none focus:ring-2 focus:ring-[#4359a6] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          >
            {isLoading
              ? mode === "signin"
                ? "Signing in..."
                : "Creating account..."
              : mode === "signin"
                ? "Sign in"
                : "Create account"}
          </button>
        </form>

        <div className="mt-6 text-center">
          {mode === "signin" ? (
            <p className="text-sm text-slate-600">
              Don&apos;t have an account?{" "}
              <button
                type="button"
                onClick={toggleMode}
                className="text-[#4359a6] hover:text-[#2d3f74] font-medium"
              >
                Sign up
              </button>
            </p>
          ) : (
            <p className="text-sm text-slate-600">
              Already have an account?{" "}
              <button
                type="button"
                onClick={toggleMode}
                className="text-[#4359a6] hover:text-[#2d3f74] font-medium"
              >
                Sign in
              </button>
            </p>
          )}
        </div>

        {import.meta.env.DEV && (
          <div className="mt-4 rounded-md bg-amber-50 p-3 text-xs text-amber-800">
            <strong>Dev mode:</strong> In development, you may be auto-signed in
            with a mock user.
          </div>
        )}
      </div>
    </div>
  );
}
